'use client';

import { useCallback } from 'react';
import { MicrophoneIcon, PhoneXMarkIcon } from '@heroicons/react/24/solid';
import { useGeminiVoice } from '@hooks/voice/useGeminiVoice';
import type { GeminiVoiceOptions } from '@hooks/voice/useGeminiVoice';
import { cn } from '@utils/cn';

interface GeminiVoiceTriggerProps extends GeminiVoiceOptions {
  className?: string;
}

export default function GeminiVoiceTrigger({
  agentName,
  promptParts,
  firstMessage,
  maxRetries,
  className,
}: GeminiVoiceTriggerProps) {
  const { status, start, stop } = useGeminiVoice({
    agentName,
    promptParts,
    firstMessage,
    maxRetries,
  });

  const isIdle = status === 'idle' || status === 'disconnected' || status === 'error';
  const isConnecting = status === 'connecting';
  const isConnected = status === 'connected';
  const isActive = !isIdle;

  const handleClick = useCallback(() => {
    if (isIdle) {
      void start();
    } else {
      stop();
    }
  }, [isIdle, start, stop]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isActive ? 'End voice conversation' : 'Start voice conversation'}
      className={cn(
        'relative flex items-center justify-center rounded-full transition-all duration-300',
        isIdle && 'animate-pulse',
        className,
        isActive && 'bg-red-500 hover:bg-red-600',
      )}
    >
      {isConnected && (
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-500/20" />
      )}
      {isConnecting ? (
        <span className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : isActive ? (
        <PhoneXMarkIcon className="size-5" />
      ) : (
        <MicrophoneIcon className="size-5" />
      )}
    </button>
  );
}
